import { useEffect, useRef, type ReactNode } from "react";
import { trackEvent } from "@/lib/analytics";

const fired = new Set<string>();

/**
 * Fires `section_view` once per name per page load, when the wrapped
 * element first becomes >=40% visible. Use to measure how deep landing
 * page visitors actually scroll.
 */
export default function SectionTracker({
  name,
  children,
  className,
}: {
  name: string;
  children: ReactNode;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (fired.has(name)) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.4) {
            if (fired.has(name)) return;
            fired.add(name);
            trackEvent("section_view", { section: name });
            observer.disconnect();
          }
        }
      },
      { threshold: [0.4] },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [name]);

  return (
    <div ref={ref} className={className} data-section={name}>
      {children}
    </div>
  );
}